import * as React from 'react';
import { WorkHistoryNode, CallStatusEnum, WorkStatusEnum } from '../../../schema';
import { workStatusTag } from '../../helpers/status';
import { renderCalendarFormat } from '../../helpers/date';
import { Card, Button } from 'sha-el-design/lib';
import './callingStyles';

/**
 * Right-hand panel on the active calling screen: allocation status, progress,
 * and the retailer queue.
 *
 * Was a stack of bare <h4>s plus a queue rendered as full-width solid colour
 * bars (#3c2 / #23c / #c32 / #ccc) with white text on mid-tone fills -- a long
 * queue became a wall of colour. Status is now carried by a dot, and the
 * caller's current position in the queue is marked.
 */

const QUEUE = {
  [CallStatusEnum.ANSWERED]: { mod: 'answered', label: 'Answered' },
  [CallStatusEnum.NO_REPLY]: { mod: 'noreply', label: 'No reply' },
  [CallStatusEnum.WRONG_NUMBER]: { mod: 'wrong', label: 'Wrong no.' },
  [CallStatusEnum.REJECTED]: { mod: 'rejected', label: 'Rejected' },
};

export const WorkCompletionStatus: React.StatelessComponent<Props> = (props) => {
  const { work, isHoldButtonDisabled, isFinishWorkDisabled } = props;
  const calls = work.calls || [];
  const done = calls.filter((v) => v.callStatus !== null).length;
  const pct = calls.length ? Math.round((done / calls.length) * 100) : 0;
  const allDone = calls.every((v) => v.callStatus !== null);
  // The first entry still open is the one the caller is on -- same rule the
  // retailer selector uses.
  const currentIndex = calls.findIndex(
    (v) => v.callStatus === null || v.callStatus === CallStatusEnum.NO_REPLY,
  );

  return (
    <div className='cs-call'>
      <Card
        title='Status'
        footer={
          <>
            <Button
              type='danger'
              onClick={props.setToOnHold}
              disabled={isHoldButtonDisabled}
            >
              Set To On Hold
            </Button>
            {allDone && (
              <Button
                onClick={props.finishWork}
                type='default'
                disabled={isFinishWorkDisabled}
              >
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Finish Work</span>
              </Button>
            )}
          </>
        }
      >
        <div className='cs-call__progress'>
          <div className='cs-call__bar'>
            <span style={{ width: `${pct}%` }} />
          </div>
          <div className='cs-call__figures'>
            <span><b>{done}</b> of {calls.length} retailers processed</span>
            <span>{pct}%</span>
          </div>
        </div>

        <div className='cs-call__row'>
          <span className='cs-call__row-label'>Assigned by</span>
          <span className='cs-call__row-value'>{work.work.supervisor.employee.name}</span>
        </div>
        <div className='cs-call__row'>
          <span className='cs-call__row-label'>Status</span>
          <span className='cs-call__row-value'>
            {workStatusTag(work.onHold ? WorkStatusEnum.ON_HOLD : WorkStatusEnum.DOING)}
          </span>
        </div>
        <div className='cs-call__row'>
          <span className='cs-call__row-label'>Date assigned</span>
          <span className='cs-call__row-value'>{renderCalendarFormat(work.work.dateAssigned)}</span>
        </div>
        <div className='cs-call__row'>
          <span className='cs-call__row-label'>Deadline</span>
          <span className='cs-call__row-value'>{renderCalendarFormat(work.work.deadline)}</span>
        </div>
        <div className='cs-call__row'>
          <span className='cs-call__row-label'>Total calls made</span>
          <span className='cs-call__row-value'>{work.totalCallsMade}</span>
        </div>
      </Card>

      <Card title={`Retailers (${done}/${calls.length})`}>
        <ul className='cs-call__queue'>
          {calls.map((call, index) => {
            const meta = QUEUE[call.callStatus as any];
            return (
              <li
                className={
                  'cs-call__q' +
                  (meta ? ` cs-call__q--${meta.mod}` : '') +
                  (index === currentIndex ? ' cs-call__q--current' : '')
                }
                key={index}
              >
                <span className='cs-call__q-dot' />
                <span className='cs-call__q-name'>{call.retailer.firm}</span>
                <span className='cs-call__q-state'>
                  {index === currentIndex ? 'Now' : (meta ? meta.label : 'Pending')}
                </span>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
};

interface Props {
  work: WorkHistoryNode;
  finishWork: () => void;
  setToOnHold: () => void;
  isHoldButtonDisabled: boolean;
  isFinishWorkDisabled: boolean;
}
